"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Tag } from "lucide-react";
function BlogListConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const containerRef = useRef<HTMLDivElement>(null);
  if (loading && posts.length === 0) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 animate-pulse">
            <div className="aspect-[16/10] bg-muted/20 rounded-lg w-full"></div>
            <div className="h-4 w-1/3 bg-muted/20 rounded-lg"></div>
            <div className="h-8 w-full bg-muted/20 rounded-lg"></div>
            <div className="h-4 w-2/3 bg-muted/20 rounded-lg"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-destructive py-20 text-center font-default bg-destructive/5 rounded-lg border border-destructive/20">
        Failed to load cultural stories. Please try again later.
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-muted-foreground py-20 text-center font-default bg-muted/10 rounded-lg border border-border">
        No insights available at the moment.
      </div>;
  }
  return <div ref={containerRef} className="relative w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
        {posts.map((post, i) => <article key={post.id} data-index={i} className="group flex flex-col h-full">
            <Link to={post.link || "#"} className="block overflow-hidden rounded-lg mb-6 aspect-[16/10] relative">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full bg-primary/5 flex items-center justify-center group-hover:bg-primary/10 transition-colors duration-500">
                  <Tag className="w-12 h-12 text-primary/20" />
                </div>}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </Link>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-secondary">
                <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate>
                <span>
                  {post.date ? new Date(post.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              }) : "Latest"}
                </span>
              </div>
              {post.categories && post.categories.length > 0 && <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-primary/60">
                  <span className="w-1 h-1 rounded-lg bg-border"></span>
                  <span>{post.categories[0].name}</span>
                </div>}
            </div>

            <Link to={post.link || "#"} className="flex-grow">
              <h3 className="text-2xl md:text-3xl font-serif font-semibold leading-tight group-hover:text-primary transition-colors duration-300 mb-4 line-clamp-2">
                {post.title}
              </h3>
              {post.excerpt && <div className="text-muted-foreground text-sm leading-relaxed line-clamp-3 mb-6 font-default" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
            </Link>

            <Link to={post.link || "#"} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-primary group/link">
              <span>Read Story</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1 text-accent" />
            </Link>
          </article>)}
      </div>

      {hasNext && <div className="mt-24 flex justify-center">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="rounded-lg px-12 py-7 text-xs uppercase tracking-widest font-bold border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all shadow-md min-w-[240px]">
            {isRefetching ? "Loading Stories..." : "Explore More Stories"}
          </Button>
        </div>}
    </div>;
}
export default function DesignThinkingInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="hong-kong-insights" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="max-w-screen-2xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
          <div className="max-w-2xl">
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold tracking-tighter leading-[0.9] text-primary mb-8">
              Hong Kong <br />
              <span className="text-secondary/40">Insights</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
              Curated stories from the heart of the city, exploring culture, team dynamics, and the hidden gems of the Pearl of the Orient.
            </p>
          </div>
          <div className="hidden lg:block pb-2">
            <div className="w-24 h-1 bg-accent rounded-lg"></div>
          </div>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6
      }}>
          <BlogListConsumer />
        </PostsProvider>
      </div>
    </section>;
}